'use client';
import { AnimatePresence, type HTMLMotionProps, type Variants } from 'motion/react';
import * as m from 'motion/react-m';
import { forwardRef, useState, type ReactNode } from 'react';
import { revealVariants } from '../../lib/motion';
import { useEffects } from '../../lib/useEffects';

type DivMotionProps = Omit<HTMLMotionProps<'div'>, 'ref' | 'initial' | 'animate' | 'exit' | 'transition' | 'variants'>;

interface MotionRevealProps extends DivMotionProps {
  children: ReactNode;
  delay?: number;
}

/** A small route/section entrance that automatically becomes opacity-only or static. */
export function MotionReveal({ children, delay = 0, ...props }: MotionRevealProps) {
  const { resolvedMode, motion } = useEffects();
  const variants = resolvedMode === 'reduced'
    ? { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } }
    : revealVariants;

  return (
    <m.div
      {...props}
      initial={resolvedMode === 'off' ? false : 'hidden'}
      animate="visible"
      exit="exit"
      variants={variants}
      transition={{ ...motion?.transition, delay }}
    >
      {children}
    </m.div>
  );
}

/** Keeps structural UI changes spatially understandable. Lists normally use popLayout so the
 * remaining rows reflow immediately while the removed row finishes its short exit. */
export function MotionPresence({ children, mode = 'popLayout' }: {
  children: ReactNode;
  mode?: 'sync' | 'wait' | 'popLayout';
}) {
  const { motionEnabled } = useEffects();
  return <AnimatePresence initial={false} mode={motionEnabled ? mode : 'sync'}>{children}</AnimatePresence>;
}

const collapseVariants = {
  full: { open: { height: 'auto', opacity: 1 }, closed: { height: 0, opacity: 0 } },
  reduced: { open: { opacity: 1 }, closed: { opacity: 0 } },
} satisfies Record<'full' | 'reduced', Variants>;

/** A region that folds away and back under `open`. While it closes, its children are the CURRENT ones, so
 *  content that leaves the region in the same render is drawn once, never frozen in the closing copy. The
 *  children leave the DOM when the fold finishes, and a closing region is `inert` at once, so a folded
 *  region is neither focusable nor read. Full folds height and opacity, reduced fades only, off stops at
 *  once. A region that is open or closed on its first render does not animate. */
export function MotionCollapse({ open, children }: { open: boolean; children: ReactNode }) {
  const { resolvedMode, motion } = useEffects();
  const [drawn, setDrawn] = useState(open);
  if (open && !drawn) setDrawn(true);
  if (!open && drawn && resolvedMode === 'off') setDrawn(false);

  if (resolvedMode === 'off') return <div>{drawn ? children : null}</div>;
  return (
    <m.div
      className="overflow-hidden"
      inert={!open}
      initial={false}
      animate={open ? 'open' : 'closed'}
      variants={collapseVariants[resolvedMode]}
      transition={motion?.transition}
      onAnimationComplete={() => { if (!open) setDrawn(false); }}
    >
      {drawn ? children : null}
    </m.div>
  );
}

/** Opacity-only enter and exit for a label that swaps in place inside a `MotionPresence`, which owns the
 *  timing of the exit. It forwards its ref: the popLayout presence measures the leaving label through it, so
 *  the label leaves the flow while it fades. Off renders a plain element, so the swap is instant and nothing waits. */
export const MotionFade = forwardRef<HTMLSpanElement, { children: ReactNode; className?: string }>(function MotionFade({ children, className }, ref) {
  const { resolvedMode, motion } = useEffects();
  if (resolvedMode === 'off') return <span ref={ref} className={className}>{children}</span>;
  return (
    <m.span
      ref={ref}
      className={className}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={motion?.transition}
    >
      {children}
    </m.span>
  );
});

/** One reorderable/present item. It becomes opacity-only when effects are reduced and static when off. */
export const MotionLayoutItem = forwardRef<HTMLDivElement, DivMotionProps & {
  children: ReactNode;
  layoutId?: string;
}>(function MotionLayoutItem({ children, layoutId, ...props }, ref) {
  const { resolvedMode, motion } = useEffects();
  return (
    <m.div
      ref={ref}
      {...props}
      layout={resolvedMode === 'full' ? 'position' : false}
      layoutId={resolvedMode === 'full' ? layoutId : undefined}
      initial={resolvedMode === 'off' ? false : { opacity: 0, y: resolvedMode === 'full' ? 6 : 0 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: resolvedMode === 'full' ? -4 : 0 }}
      transition={motion?.transition}
    >
      {children}
    </m.div>
  );
});
